/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import {
  changePassword,
  getGoogleCalendarConnectUrl,
  getMicrosoftLoginUrl,
  type NotificationPreferences,
  type GoogleCalendar,
  type MicrosoftCalendar,
  type SubscribedCalendar,
} from '../services';
import { useSettingsStore, useFamilyStore, useUserStore } from '../stores';
import { useMessage } from './useMessage';

export function useSettingsPage() {
  // Store state & actions
  const {
    preferences,
    googleCalendars = [],
    microsoftCalendars = [],
    subscribedCalendars = [],
    microsoftStatus,
    isLoading, isSyncing, error: settingsError,
    fetchAll: fetchSettings,
    updatePreference,
    syncGoogle, toggleGoogleCalendar,
    syncMicrosoft, toggleMicrosoftCalendar,
    addSubscribed, updateSubscribed, removeSubscribed, syncSubscribed, retrySubscribed,
  } = useSettingsStore();
  const { members: familyMembers = [], fetchMembers } = useFamilyStore();
  const { user, logout: userLogout } = useUserStore();
  const { message, showSuccess, showError } = useMessage();

  // Microsoft sync uses separate loading state
  const [isSyncingMicrosoft, setIsSyncingMicrosoft] = useState(false);

  // Add calendar form
  const [showAddCalendar, setShowAddCalendar] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [newCalendarUrl, setNewCalendarUrl] = useState('');
  const [newCalendarMember, setNewCalendarMember] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Edit calendar URL state
  const [editingCalendarId, setEditingCalendarId] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState('');

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Announce what this surface's own load left behind, once, when it settles.
  // Never replay an error already in a store before the load ran: the sheet
  // mounts on every open, and replaying made one failed members fetch an
  // alert on every later open (s6c r926 f2). Each store's error is the load's
  // own result once it settles: fetchMembers clears it on success and sets it
  // on failure, and the settings store's fetchAll writes it on every run and
  // skips only while its data is fresh, when the error is already null.
  useEffect(() => {
    let mounted = true;
    Promise.all([fetchSettings(), fetchMembers()])
      .catch(() => undefined)
      .then(() => {
        if (!mounted) return;
        const error = useSettingsStore.getState().error || useFamilyStore.getState().error;
        if (error) showError(error);
      });
    return () => { mounted = false; };
  }, []);

  const handleToggle = useCallback(async (key: keyof NotificationPreferences) => {
    if (!preferences) return;
    try {
      await updatePreference(key, !preferences[key]);
    } catch {
      showError('Failed to save');
    }
  }, [preferences, updatePreference, showError]);

  const handleTimeChange = useCallback(async (time: string) => {
    if (!preferences) return;
    try {
      await updatePreference('morning_briefing_time', time);
    } catch {
      showError('Failed to save');
    }
  }, [preferences, updatePreference, showError]);

  const handleMinutesChange = useCallback(async (minutes: number) => {
    if (!preferences) return;
    try {
      await updatePreference('leave_reminder_minutes', minutes);
    } catch {
      showError('Failed to save');
    }
  }, [preferences, updatePreference, showError]);

  const handleSyncGoogle = useCallback(async () => {
    try {
      await syncGoogle();
      showSuccess('Calendars synced');
    } catch {
      showError('Failed to sync');
    }
  }, [syncGoogle, showSuccess, showError]);

  const handleToggleGoogleCalendar = useCallback(async (cal: GoogleCalendar) => {
    const newEnabled = !cal.is_enabled;
    try {
      await toggleGoogleCalendar(cal);
      showSuccess(newEnabled ? `${cal.name} enabled` : `${cal.name} disabled`);
    } catch {
      showError('Failed to update calendar');
    }
  }, [toggleGoogleCalendar, showSuccess, showError]);

  const handleSyncMicrosoft = useCallback(async () => {
    setIsSyncingMicrosoft(true);
    try {
      await syncMicrosoft();
      showSuccess('Microsoft calendars synced');
    } catch {
      showError('Failed to sync Microsoft calendars');
    } finally {
      setIsSyncingMicrosoft(false);
    }
  }, [syncMicrosoft, showSuccess, showError]);

  const handleToggleMicrosoftCalendar = useCallback(async (cal: MicrosoftCalendar) => {
    const newEnabled = !cal.is_enabled;
    try {
      await toggleMicrosoftCalendar(cal);
      showSuccess(newEnabled ? `${cal.name} enabled` : `${cal.name} disabled`);
    } catch {
      showError('Failed to update calendar');
    }
  }, [toggleMicrosoftCalendar, showSuccess, showError]);

  const handleConnectMicrosoft = useCallback(() => {
    window.location.href = getMicrosoftLoginUrl();
  }, []);

  const handleConnectGoogle = useCallback(() => {
    window.location.href = getGoogleCalendarConnectUrl();
  }, []);

  const handleChangePassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      showError('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      showSuccess('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password';
      showError(msg);
    } finally {
      setIsChangingPassword(false);
    }
  }, [currentPassword, newPassword, confirmPassword, showSuccess, showError]);

  const handleSyncSubscribed = useCallback(async () => {
    try {
      await syncSubscribed();
      showSuccess('Calendars synced');
    } catch {
      showError('Failed to sync');
    }
  }, [syncSubscribed, showSuccess, showError]);

  const handleAddCalendar = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCalendarName.trim() || !newCalendarUrl.trim()) return;

    setIsAdding(true);
    try {
      let url = newCalendarUrl.trim();
      if (url.startsWith('webcal://')) {
        url = url.replace('webcal://', 'https://');
      }
      const calendar = await addSubscribed(
        newCalendarName.trim(),
        url,
        newCalendarMember || undefined
      );
      setNewCalendarName('');
      setNewCalendarUrl('');
      setNewCalendarMember(null);
      setShowAddCalendar(false);
      showSuccess(`Added ${calendar.name}. Syncing...`);

      try {
        await syncSubscribed();
        showSuccess(`${calendar.name} synced`);
      } catch {
        showError('Calendar added but sync failed');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add calendar';
      showError(msg);
    } finally {
      setIsAdding(false);
    }
  }, [newCalendarName, newCalendarUrl, newCalendarMember, addSubscribed, syncSubscribed, showSuccess, showError]);

  const handleAssignMember = useCallback(async (calendar: SubscribedCalendar, memberId: number | null) => {
    try {
      await updateSubscribed(calendar.id, { family_member: memberId });
      const memberName = memberId ? familyMembers.find(m => m.id === memberId)?.name : null;
      showSuccess(memberName ? `Assigned to ${memberName}` : 'Unassigned');
    } catch {
      showError('Failed to update assignment');
    }
  }, [updateSubscribed, familyMembers, showSuccess, showError]);

  const handleRetryCalendar = useCallback(async (calendar: SubscribedCalendar) => {
    try {
      await retrySubscribed(calendar.id);
      showSuccess('Synced');
    } catch (err: unknown) {
      // The server writes a person-readable reason on failure; surface it.
      const body = (err as { body?: { error?: string } })?.body;
      showError(body?.error || 'Sync failed — the feed may be dead.');
    }
  }, [retrySubscribed, showSuccess, showError]);

  const handleRemoveCalendar = useCallback(async (calendar: SubscribedCalendar) => {
    if (!window.confirm(`Remove ${calendar.name}?`)) return;
    try {
      await removeSubscribed(calendar.id);
      showSuccess('Calendar removed');
    } catch {
      showError('Failed to remove');
    }
  }, [removeSubscribed, showSuccess, showError]);

  const handleEditUrl = useCallback((calendar: SubscribedCalendar) => {
    setEditingCalendarId(calendar.id);
    setEditingUrl(calendar.url);
  }, []);

  const handleSaveUrl = useCallback(async (calendar: SubscribedCalendar) => {
    if (!editingUrl.trim()) return;

    let url = editingUrl.trim();
    if (url.startsWith('webcal://')) {
      url = url.replace('webcal://', 'https://');
    }

    try {
      await updateSubscribed(calendar.id, { url });
      setEditingCalendarId(null);
      setEditingUrl('');
      showSuccess('URL updated. Syncing...');

      try {
        await syncSubscribed();
        showSuccess('Calendar synced');
      } catch {
        showError('URL saved but sync failed');
      }
    } catch {
      showError('Failed to update URL');
    }
  }, [editingUrl, updateSubscribed, syncSubscribed, showSuccess, showError]);

  const handleCancelEditUrl = useCallback(() => {
    setEditingCalendarId(null);
    setEditingUrl('');
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await userLogout();
      window.location.href = '/login';
    } catch {
      showError('Failed to sign out');
    }
  }, [userLogout, showError]);

  return {
    // State
    user,
    preferences,
    googleCalendars,
    microsoftCalendars,
    subscribedCalendars,
    microsoftStatus,
    familyMembers,
    isLoading,
    isSyncing,
    isSyncingMicrosoft,
    message,
    showError,
    settingsError,
    fetchSettings,

    // Add calendar form state
    showAddCalendar,
    setShowAddCalendar,
    newCalendarName,
    setNewCalendarName,
    newCalendarUrl,
    setNewCalendarUrl,
    newCalendarMember,
    setNewCalendarMember,
    isAdding,

    // Edit URL state
    editingCalendarId,
    editingUrl,
    setEditingUrl,

    // Password state
    showChangePassword,
    setShowChangePassword,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    isChangingPassword,

    // Handlers
    handleToggle,
    handleTimeChange,
    handleMinutesChange,
    handleSyncGoogle,
    handleToggleGoogleCalendar,
    handleSyncMicrosoft,
    handleToggleMicrosoftCalendar,
    handleConnectMicrosoft,
    handleConnectGoogle,
    handleChangePassword,
    handleSyncSubscribed,
    handleAddCalendar,
    handleAssignMember,
    handleRemoveCalendar,
    handleRetryCalendar,
    handleEditUrl,
    handleSaveUrl,
    handleCancelEditUrl,
    handleSignOut,
  };
}

export type UseSettingsPageReturn = ReturnType<typeof useSettingsPage>;
